import type { EntityType, QueryFilter } from '@mariana/labos-client';
import type { LabSelection } from '../LabContext';

export interface ColumnDef {
  /**
   * Attribute key. Use dot-notation (`fkAttr.targetAttr`) for cross-entity
   * columns (e.g. `sample_id.sample_name`). The FK attribute's
   * `referenced_entity_type` is resolved at runtime from the attribute cache.
   */
  key: string;
  label: string;
  render?: 'date' | 'link' | 'state';
  /** Pull a nested value from a JSON attribute (e.g. worksheet_data). */
  extract?: { from: string; path: string };
  /** Whether this column supports server-side sorting. Defaults to true; false for extract columns. */
  sortable?: boolean;
}

export interface ViewDefinition {
  id: string;
  label: string;
  description?: string;
  entityType: EntityType;
  filters: QueryFilter[];
  columns: ColumnDef[];
  resultViewer?: string;
  defaultSort?: { by: string; order: 'asc' | 'desc' };
}

const TEST_BASE_COLUMNS: ColumnDef[] = [
  { key: 'id', label: 'Test ID' },
  { key: 'sample_id', label: 'Sample', render: 'link' },
  { key: 'sample_id.sample_name', label: 'Sample Name' },
  { key: 'sample_id.sample_point', label: 'Sample Point' },
  { key: 'sample_id.order_id', label: 'Order' },
  { key: 'sample_id.mm_los_experiment_name', label: 'Experiment' },
  { key: 'sample_id.mm_los_experiment_campaign_name', label: 'Campaign' },
  { key: 'assay_id.title', label: 'Assay' },
  { key: 'state', label: 'State', render: 'state' },
  { key: 'sample_id.collection_timestamp', label: 'Collected', render: 'date' },
  { key: 'date_created', label: 'Created', render: 'date' },
  { key: 'complete_date', label: 'Completed', render: 'date' },
];

export const VIEW_REGISTRY: Record<string, ViewDefinition> = {
  icp: {
    id: 'icp',
    label: 'ICP',
    description: 'ARCOS ICP elemental analysis tests',
    entityType: 'test',
    filters: [{ attribute_name: 'assay_id', operator: 'IN', value: [4, 15, 16, 24, 25, 28, 29, 31, 42] }],
    columns: [
      ...TEST_BASE_COLUMNS,
      ...['Al', 'As', 'B', 'Ba', 'Be', 'Br', 'Ca', 'Cd', 'Cl', 'Co', 'Cr', 'Cu', 'Fe', 'I', 'K', 'Li', 'Mg', 'Mn', 'Na', 'Ni', 'P', 'Pb', 'S', 'Sb', 'Si', 'Sn', 'Sr', 'Ti', 'Zn'].map(el => ({
        key: `icp_${el.toLowerCase()}`,
        label: el,
        extract: { from: 'worksheet_data', path: `report_export_${el.toLowerCase()}.value` },
        sortable: false,
      })),
    ],
    resultViewer: 'icp',
    defaultSort: { by: 'id', order: 'desc' },
  },
  nmr: {
    id: 'nmr',
    label: 'NMR',
    description: 'NanoNord NMR tests',
    entityType: 'test',
    filters: [{ attribute_name: 'assay_id', operator: 'IN', value: [10, 23, 30] }],
    columns: [
      ...TEST_BASE_COLUMNS,
      { key: 'nmr_li', label: 'Li', extract: { from: 'worksheet_data', path: 'Li_result' }, sortable: false },
      { key: 'nmr_na', label: 'Na', extract: { from: 'worksheet_data', path: 'Na_result' }, sortable: false },
      { key: 'nmr_cl', label: 'Cl', extract: { from: 'worksheet_data', path: 'Cl' }, sortable: false },
      { key: 'nmr_b', label: 'B', extract: { from: 'worksheet_data', path: 'B_result' }, sortable: false },
      { key: 'nmr_br', label: 'Br', extract: { from: 'worksheet_data', path: 'Br_result' }, sortable: false },
    ],
    resultViewer: 'nmr',
    defaultSort: { by: 'id', order: 'desc' },
  },
  shimadzu: {
    id: 'shimadzu',
    label: 'Shimadzu',
    description: 'Shimadzu TOC/TN carbon & nitrogen analysis tests',
    entityType: 'test',
    filters: [{ attribute_name: 'assay_id', operator: 'IN', value: [11, 12, 14, 19, 21, 22] }],
    columns: [
      ...TEST_BASE_COLUMNS,
      ...['TOC', 'TC', 'IC', 'POC', 'NPOC', 'TN'].map(param => ({
        key: `shimadzu_${param.toLowerCase()}`,
        label: param,
        extract: { from: 'worksheet_data', path: `report_export_result_${param.toLowerCase()}_.value` },
        sortable: false,
      })),
    ],
    resultViewer: 'shimadzu',
    defaultSort: { by: 'id', order: 'desc' },
  },
};

export const ENTITY_COLUMNS: Record<EntityType, ColumnDef[]> = {
  sample: [
    { key: 'id', label: 'ID' },
    { key: 'sample_name', label: 'Name' },
    { key: 'sample_point', label: 'Sample Point' },
    { key: 'collection_site', label: 'Site' },
    { key: 'sample_matrix', label: 'Matrix' },
    { key: 'order_id', label: 'Order', render: 'link' },
    { key: 'complete', label: 'Complete' },
    { key: 'date_received', label: 'Received', render: 'date' },
    { key: 'test_count', label: 'Tests' },
  ],
  test: [
    { key: 'id', label: 'ID' },
    { key: 'sample_id', label: 'Sample', render: 'link' },
    { key: 'sample_id.sample_name', label: 'Sample Name' },
    { key: 'assay_id.title', label: 'Assay' },
    { key: 'state', label: 'State', render: 'state' },
    { key: 'start_date', label: 'Started', render: 'date' },
    { key: 'complete_date', label: 'Completed', render: 'date' },
  ],
  assay: [
    { key: 'id', label: 'ID' },
    { key: 'title', label: 'Title' },
    { key: 'active', label: 'Active' },
  ],
  order: [
    { key: 'id', label: 'ID' },
    { key: 'submitted_by', label: 'Submitted By' },
    { key: 'lab_id.name', label: 'Lab' },
    { key: 'state', label: 'State', render: 'state' },
    { key: 'date_created', label: 'Created', render: 'date' },
    { key: 'date_received', label: 'Received', render: 'date' },
  ],
  attachment: [
    { key: 'id', label: 'ID' },
    { key: 'filename', label: 'Filename' },
    { key: 'content_type', label: 'Type' },
    { key: 'parent_entity_type', label: 'Parent Type' },
    { key: 'parent_entity_id', label: 'Parent ID' },
    { key: 'file_size', label: 'Size' },
  ],
  lab: [
    { key: 'id', label: 'ID' },
    { key: 'name', label: 'Name' },
    { key: 'description', label: 'Description' },
    { key: 'timezone', label: 'Timezone' },
  ],
};

/**
 * Per-lab column overrides. Only entity types that differ between labs need entries.
 * Any entity type not listed here falls back to ENTITY_COLUMNS.
 */
const ENTITY_COLUMNS_BY_LAB: Partial<Record<LabSelection, Partial<Record<EntityType, ColumnDef[]>>>> = {
  2: { 
    sample: [
      { key: 'id', label: 'ID' },
      { key: 'sample_name', label: 'Name' },
      { key: 'sample_description', label: 'Description' },
      { key: 'collection_site', label: 'Collection Site' },
      { key: 'collection_datestamp', label: 'Collected' },
      { key: 'complete', label: 'Complete' },
      { key: 'test_count', label: 'Tests' },
    ],
  },
};

export function getEntityColumns(entityType: EntityType, lab?: LabSelection): ColumnDef[] {
  if (lab) {
    const override = ENTITY_COLUMNS_BY_LAB[lab]?.[entityType];
    if (override) return override;
  }
  return ENTITY_COLUMNS[entityType] ?? [];
}

const ASSAY_ID_TO_VIEWER: Record<number, string> = {};
for (const view of Object.values(VIEW_REGISTRY)) {
  if (view.resultViewer) {
    for (const filter of view.filters) {
      if (filter.attribute_name === 'assay_id' && Array.isArray(filter.value)) {
        for (const id of filter.value) {
          if (typeof id === 'number') {
            ASSAY_ID_TO_VIEWER[id] = view.resultViewer;
          }
        }
      }
    }
  }
}

export function getResultViewer(assayId: number): string | undefined {
  return ASSAY_ID_TO_VIEWER[assayId];
}
